import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutWrapper from '@components/layout/UILayout';
import ModuleSelector from '@components/Templates/ModuleSelector';
import { BetweenItemAddButton } from '@components/ui/buttons/BetweenItemAddButton';
import { CenteredSpinner } from '@components/ui/feedback/CenteredSpinner';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useToolbar } from '@features/toolbar';
import { ArrowBack } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { RoundedButton } from '@styles/CustomButtons';
import type { AvailableModule } from '@ts/types';
import { useSnackbar } from 'notistack';

import { useNoteWithPolling, useTemplateUndo } from '../store';
import {
  notesApi,
  useAddSectionsToNoteMutation,
  useDeleteSectionMutation,
  useReorderSectionsMutation,
  useUpdateNoteTemplateMutation,
} from '../store/api';

import { EditableTemplateHeading } from './template-editor/EditableTemplateHeading';
import { TemplateSectionItem } from './template-editor/TemplateSectionItem';
import type { TemplateSection } from './template-editor/types';

export const EditNoteTemplate: React.FC = () => {
  // Get noteId from URL params
  const { noteId } = useParams<{ noteId: string }>();

  // Fetch note data via RTK Query with auto-polling for loading sections
  const { data: noteData, isLoading, isError } = useNoteWithPolling(noteId, { skip: !noteId });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { hide: hideToolbar, show: showToolbar } = useToolbar();

  // Event tracking
  const { template, noteSection, error: trackError } = useEventTracking();

  // All hooks must be called before any conditional returns
  const [addSections] = useAddSectionsToNoteMutation();
  const [reorderSections] = useReorderSectionsMutation();
  const [deleteSection] = useDeleteSectionMutation();
  const [updateNoteTemplate] = useUpdateNoteTemplateMutation();
  const [isModuleSelectorOpen, setIsModuleSelectorOpen] = useState(false);
  const [insertionIndex, setInsertionIndex] = useState<number>(-1);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [validDrop, setValidDrop] = useState(false);

  useEffect(() => {
    hideToolbar();
    return () => {
      showToolbar();
    };
  }, [hideToolbar, showToolbar]);

  // Track template edit started
  useEffect(() => {
    if (noteData?.template?.template_id && noteId) {
      template.customizationStarted(noteData.template.template_id, {
        source: 'existing',
        initial_sections_count: noteData.content?.sections?.length || 0,
      });
    }
  }, [noteData?.template?.template_id, noteId, template]);

  // Track abandonment on unmount
  // TODO: Extend useEventTracking hook to include customizationAbandoned method
  // For now, we rely on individual action tracking (add, remove, reorder)
  useEffect(() => {
    return () => {
      // Cleanup tracking will be handled by individual actions
      // Future: Track NOTE_TEMPLATE_EDIT_ABANDONED event here
    };
  }, []);

  // Initialize undo/redo system
  const { recordAction, undo: performUndoAction } = useTemplateUndo({
    noteId,
    enableKeyboardShortcuts: true,
    onUndo: async action => {
      if (!noteId) return;

      if (action.type === 'reorder' && action.previousSectionOrder) {
        // Restore previous section order
        try {
          // Optimistically update cache
          dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (draft.content?.sections && action.previousSectionOrder) {
                // Reorder sections back to previous state
                const sectionMap = new Map(draft.content.sections.map(s => [s.section_id, s]));
                draft.content.sections = action.previousSectionOrder
                  .map(id => sectionMap.get(id))
                  .filter(Boolean) as typeof draft.content.sections;
              }
            }),
          );

          // Call API to persist
          await reorderSections({
            noteId,
            reorderSectionsRequest: {
              sections: action.previousSectionOrder,
            },
          }).unwrap();

          enqueueSnackbar('Sections restored to previous order', { variant: 'success' });
        } catch (error) {
          console.error('Failed to undo reorder:', error);
          enqueueSnackbar('Failed to undo. Please try again.', { variant: 'error' });
          trackError.track(
            error instanceof Error ? error : new Error('Unknown error'),
            'note_template_undo_reorder',
            {
              note_id: noteId,
            },
          );
        }
      } else if (
        action.type === 'remove' &&
        action.deletedSection &&
        action.deletedSectionIndex !== undefined
      ) {
        // Undo deletion: Re-add the deleted section at its original position
        try {
          // Optimistically re-add section to cache
          dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (
                draft.content?.sections &&
                action.deletedSection &&
                action.deletedSectionIndex !== undefined
              ) {
                draft.content.sections.splice(action.deletedSectionIndex, 0, action.deletedSection);
              }
            }),
          );

          // Call API to re-add section (using module_id if available)
          if (action.deletedSection.module_id) {
            await addSections({
              noteId,
              addSectionsRequest: {
                insert_index: action.deletedSectionIndex,
                module_ids: [action.deletedSection.module_id],
              },
            }).unwrap();
          }

          enqueueSnackbar('Section restored', { variant: 'success' });
        } catch (error) {
          console.error('Failed to undo deletion:', error);
          enqueueSnackbar('Failed to undo. Please try again.', { variant: 'error' });
          trackError.track(
            error instanceof Error ? error : new Error('Unknown error'),
            'note_template_undo_remove',
            {
              note_id: noteId,
            },
          );
        }
      } else if (
        action.type === 'add' &&
        action.addedModuleIds &&
        action.addedSectionIndex !== undefined
      ) {
        // Undo addition: Delete the added sections
        try {
          // Get the section IDs that were added (need to find them in current state)
          const currentSections = noteData?.content?.sections || [];
          const sectionsToDelete = currentSections.slice(
            action.addedSectionIndex,
            action.addedSectionIndex + action.addedModuleIds.length,
          );

          // Optimistically remove sections from cache
          dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (
                draft.content?.sections &&
                action.addedSectionIndex !== undefined &&
                action.addedModuleIds
              ) {
                draft.content.sections.splice(
                  action.addedSectionIndex,
                  action.addedModuleIds.length,
                );
              }
            }),
          );

          // Delete each section via API
          for (const section of sectionsToDelete) {
            await deleteSection({
              noteId,
              sectionId: section.section_id,
            }).unwrap();
          }

          enqueueSnackbar('Added sections removed', { variant: 'success' });
        } catch (error) {
          console.error('Failed to undo addition:', error);
          enqueueSnackbar('Failed to undo. Please try again.', { variant: 'error' });
          trackError.track(
            error instanceof Error ? error : new Error('Unknown error'),
            'note_template_undo_add',
            {
              note_id: noteId,
            },
          );
        }
      }
    },
    onRedo: async action => {
      if (!noteId) return;

      if (action.type === 'reorder' && action.sectionOrder) {
        // Reapply section order
        try {
          // Optimistically update cache
          dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (draft.content?.sections && action.sectionOrder) {
                // Reorder sections to redone state
                const sectionMap = new Map(draft.content.sections.map(s => [s.section_id, s]));
                draft.content.sections = action.sectionOrder
                  .map(id => sectionMap.get(id))
                  .filter(Boolean) as typeof draft.content.sections;
              }
            }),
          );

          // Call API to persist
          await reorderSections({
            noteId,
            reorderSectionsRequest: {
              sections: action.sectionOrder,
            },
          }).unwrap();

          enqueueSnackbar('Sections reordered', { variant: 'success' });
        } catch (error) {
          console.error('Failed to redo reorder:', error);
          enqueueSnackbar('Failed to redo. Please try again.', { variant: 'error' });
          trackError.track(
            error instanceof Error ? error : new Error('Unknown error'),
            'note_template_redo_reorder',
            {
              note_id: noteId,
            },
          );
        }
      } else if (action.type === 'remove' && action.deletedSection) {
        // Redo deletion: Delete the section again
        try {
          const sectionToDelete = action.deletedSection;

          // Optimistically remove section from cache
          dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (draft.content?.sections) {
                const index = draft.content.sections.findIndex(
                  s => s.section_id === sectionToDelete.section_id,
                );
                if (index !== -1) {
                  draft.content.sections.splice(index, 1);
                }
              }
            }),
          );

          // Call API to delete section
          await deleteSection({
            noteId,
            sectionId: sectionToDelete.section_id,
          }).unwrap();

          enqueueSnackbar('Section deleted', { variant: 'success' });
        } catch (error) {
          console.error('Failed to redo deletion:', error);
          enqueueSnackbar('Failed to redo. Please try again.', { variant: 'error' });
          trackError.track(
            error instanceof Error ? error : new Error('Unknown error'),
            'note_template_redo_remove',
            {
              note_id: noteId,
            },
          );
        }
      } else if (
        action.type === 'add' &&
        action.addedModuleIds &&
        action.addedSectionIndex !== undefined
      ) {
        // Redo addition: Re-add the sections
        try {
          // Optimistically add sections to cache
          dispatch(
            notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
              if (!draft.content) {
                draft.content = { sections: [] };
              }
              if (!draft.content.sections) {
                draft.content.sections = [];
              }
              if (action.addedModuleIds && action.addedSectionIndex !== undefined) {
                // Create optimistic section entries
                const newSections = action.addedModuleIds.map(moduleId => ({
                  section_id: `temp-${Date.now()}-${Math.random()}`,
                  module_id: moduleId,
                  name: 'Loading...',
                  text: '',
                  edited_text: undefined,
                }));
                draft.content.sections.splice(action.addedSectionIndex, 0, ...newSections);
              }
            }),
          );

          // Call API to re-add sections
          await addSections({
            noteId,
            addSectionsRequest: {
              insert_index: action.addedSectionIndex,
              module_ids: action.addedModuleIds,
            },
          }).unwrap();

          enqueueSnackbar('Sections added', { variant: 'success' });
        } catch (error) {
          console.error('Failed to redo addition:', error);
          enqueueSnackbar('Failed to redo. Please try again.', { variant: 'error' });
          trackError.track(
            error instanceof Error ? error : new Error('Unknown error'),
            'note_template_redo_add',
            {
              note_id: noteId,
            },
          );
        }
      }
    },
  });

  /**
   * Creates an undo action button for snackbars
   * Follows Material Design pattern with 5-second visibility window
   */
  const createUndoAction = (key: number | string) => (
    <Button
      color="inherit"
      size="small"
      onClick={() => {
        performUndoAction();
        closeSnackbar(key);
      }}
      aria-label="Undo action"
    >
      UNDO
    </Button>
  );

  // Convert noteData.content.sections to TemplateSection format - derived from props
  const sections: TemplateSection[] = useMemo(
    () =>
      noteData?.content?.sections?.map((section, index) => ({
        id: section.section_id,
        title: section.name,
        content: section.edited_text || section.text,
        order: index,
      })) || [],
    [noteData?.content?.sections],
  );

  // Calculate excluded module IDs (sections already in template)
  const excludedModuleIds = useMemo(() => {
    return noteData?.content?.sections?.map(s => s.module_id).filter(Boolean) as string[];
  }, [noteData?.content?.sections]);

  // Configure drag sensors - require 8px drag before activating
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Get template name for display
  const templateName = noteData?.template?.template_name || 'Note Template';

  // Handle loading state
  if (isLoading) {
    return <CenteredSpinner />;
  }

  // Handle error state
  if (isError || !noteData) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          height: '100%',
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Failed to load note
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Unable to load the note template. Please try again.
        </Typography>
      </Box>
    );
  }

  const handleDragStart = (event: DragStartEvent) => {
    const sectionId = event.active.id as string;
    setActiveId(sectionId);

    // Track user interaction - drag started
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    noteSection.sectionDragStarted({
      note_id: noteId,
      section_id: sectionId,
      section_index: sectionIndex,
      section_count: sections.length,
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    // Determine if this was a valid drop
    const wasValidDrop = !!(over && active.id !== over.id && noteId);

    // Set validDrop state before clearing (DragOverlay reads this)
    setValidDrop(wasValidDrop);

    // Clear all drag states immediately (no delay)
    setActiveId(null);

    if (!wasValidDrop) {
      return;
    }

    // Get current section order (before reorder)
    const previousSectionOrder = sections.map(s => s.id);

    // Calculate new order
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Create new order array
    const newSectionOrder = [...previousSectionOrder];
    const [movedItem] = newSectionOrder.splice(oldIndex, 1);
    newSectionOrder.splice(newIndex, 0, movedItem);

    // Perform optimistic update to cache
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        if (draft.content?.sections) {
          // Reorder sections in cache
          const reorderedSections = [...draft.content.sections];
          const [moved] = reorderedSections.splice(oldIndex, 1);
          reorderedSections.splice(newIndex, 0, moved);
          draft.content.sections = reorderedSections;
        }
      }),
    );

    try {
      // Call API to persist reorder
      await reorderSections({
        noteId,
        reorderSectionsRequest: {
          sections: newSectionOrder,
        },
      }).unwrap();

      // Track successful reorder outcome
      noteSection.reordered({
        note_id: noteId,
        section_count: sections.length,
        from_index: oldIndex,
        to_index: newIndex,
      });

      // Record undo action
      recordAction({
        type: 'reorder',
        description: 'Reordered sections',
        patches: [], // Not using patches for reorder
        inversePatches: [], // Not using patches for reorder
        sectionOrder: newSectionOrder,
        previousSectionOrder,
      });

      enqueueSnackbar('Sections reordered successfully', {
        variant: 'success',
        autoHideDuration: 5000,
        action: createUndoAction,
      });
    } catch (error) {
      // On error, cache will be invalidated and refetched
      console.error('Failed to reorder sections:', error);
      trackError.track(error instanceof Error ? error : new Error('Unknown error'), 'note_template_reorder_sections', {
        note_id: noteId,
      });
      enqueueSnackbar('Failed to reorder sections. Please try again.', { variant: 'error' });
    }
  };

  const handleRemove = async (id: string) => {
    if (!noteId) {
      enqueueSnackbar('Note ID is missing. Cannot delete section.', { variant: 'error' });
      return;
    }

    // Find the section being deleted
    const sectionIndex = sections.findIndex(s => s.id === id);

    // Track user interaction - remove button clicked (before validation/API call)
    const section = sections[sectionIndex];
    if (section) {
      noteSection.removeSectionClicked({
        note_id: noteId,
        section_id: id,
        section_type: section.title,
        section_index: sectionIndex,
      });
    }
    if (sectionIndex === -1) {
      enqueueSnackbar('Section not found.', { variant: 'error' });
      return;
    }

    // Get the full section data from noteData for undo
    const deletedSection = noteData?.content?.sections?.[sectionIndex];
    if (!deletedSection) {
      enqueueSnackbar('Section data not found.', { variant: 'error' });
      return;
    }

    // Optimistically remove section from cache
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        if (draft.content?.sections) {
          draft.content.sections.splice(sectionIndex, 1);
        }
      }),
    );

    try {
      // Call API to delete section
      await deleteSection({
        noteId,
        sectionId: id,
      }).unwrap();

      // Record undo action
      recordAction({
        type: 'remove',
        description: `Removed section: ${deletedSection.name}`,
        patches: [],
        inversePatches: [],
        deletedSection,
        deletedSectionIndex: sectionIndex,
      });

      // Track section removal
      noteSection.removed({
        note_id: noteId,
        section_id: id,
        section_type: deletedSection.name,
      });

      enqueueSnackbar('Section deleted successfully', {
        variant: 'success',
        autoHideDuration: 5000,
        action: createUndoAction,
      });
    } catch (error) {
      // On error, cache will be invalidated and refetched
      console.error('Failed to delete section:', error);
      trackError.track(error instanceof Error ? error : new Error('Unknown error'), 'note_template_remove_section', {
        note_id: noteId,
      });
      enqueueSnackbar('Failed to delete section. Please try again.', { variant: 'error' });
    }
  };

  const handleAdd = (afterIndex: number) => {
    // Track user interaction - button clicked
    noteSection.addSectionClicked({
      note_id: noteId,
      insertion_index: afterIndex,
      section_count: sections.length,
    });

    setInsertionIndex(afterIndex);
    setIsModuleSelectorOpen(true);
  };

  const handleAddModules = async (modules: AvailableModule[]) => {
    if (!noteId) {
      enqueueSnackbar('Note ID is missing. Cannot add sections.', { variant: 'error' });
      return;
    }

    // Calculate insertion position (0-based index for API)
    const insertPosition = insertionIndex === -1 ? 0 : insertionIndex + 1;

    // Extract module IDs
    const module_ids = modules.map(m => m.module_id);

    // Perform optimistic update
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        // Optimistically add new sections to the cache
        if (!draft.content) {
          draft.content = { sections: [] };
        }
        if (!draft.content.sections) {
          draft.content.sections = [];
        }

        // Create optimistic section entries
        const newSections = modules.map(module => ({
          section_id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID
          module_id: module.module_id,
          name: module.module_name,
          text: '', // Will be populated by API response
          edited_text: undefined,
        }));

        // Insert sections at the specified position
        draft.content.sections.splice(insertPosition, 0, ...newSections);
      }),
    );

    try {
      // Call API to add sections - this will invalidate cache and refresh with real data
      await addSections({
        noteId,
        addSectionsRequest: {
          insert_index: insertPosition,
          module_ids,
        },
      }).unwrap();

      // Record undo action
      recordAction({
        type: 'add',
        description: `Added ${modules.length} section${modules.length > 1 ? 's' : ''}`,
        patches: [],
        inversePatches: [],
        addedModuleIds: module_ids,
        addedSectionIndex: insertPosition,
      });

      // Track section addition
      noteSection.added({
        note_id: noteId,
        add_method: 'module_selector',
      });

      enqueueSnackbar(
        `Added ${modules.length} section${modules.length > 1 ? 's' : ''} successfully`,
        {
          variant: 'success',
          autoHideDuration: 5000,
          action: createUndoAction,
        },
      );
      setIsModuleSelectorOpen(false);
    } catch (error) {
      // On error, cache will be invalidated and real data will be refetched
      console.error('Failed to add sections:', error);
      trackError.track(error instanceof Error ? error : new Error('Unknown error'), 'note_template_add_sections', {
        note_id: noteId,
        attempted_count: String(modules.length),
      });
      enqueueSnackbar('Failed to add sections. Please try again.', { variant: 'error' });
    }
  };

  const handleCloseModal = () => {
    setIsModuleSelectorOpen(false);
    setInsertionIndex(-1);
  };

  const handleSaveTemplateName = async (newName: string) => {
    if (!noteId) {
      enqueueSnackbar('Unable to update template name.', { variant: 'error' });
      return;
    }

    const previousName = templateName;
    const previousTemplateId = noteData?.template?.template_id;

    // Optimistically update cache
    dispatch(
      notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
        if (draft.template) {
          draft.template.template_name = newName;
        }
      }),
    );

    try {
      // Call new note-specific template API - handles forking automatically
      const result = await updateNoteTemplate({
        noteId,
        updateNoteTemplateRequest: {
          title: newName,
        },
      }).unwrap();

      // Track template name change
      if (result.template?.template_id) {
        template.sectionModified(result.template.template_id, {
          action: 'edit_title',
        });
      }

      enqueueSnackbar('Template name updated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      // Rollback on error
      dispatch(
        notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
          if (draft.template) {
            draft.template.template_name = previousName;
          }
        }),
      );

      console.error('Failed to update template name:', error);
      trackError.track(
        error instanceof Error ? error : new Error('Unknown error'),
        'note_template_update_name',
        {
          note_id: noteId,
          template_id: noteData?.template?.template_id,
        },
      );
      enqueueSnackbar('Failed to update template name. Please try again.', { variant: 'error' });
      throw error; // Re-throw so EditableTemplateHeading knows it failed
    }
  };

  // If no sections, show placeholder
  if (sections.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          height: '100%',
        }}
      >
        <EditableTemplateHeading
          templateName={templateName}
          onSave={handleSaveTemplateName}
          canEdit={noteData?.can_edit_template ?? false}
          variant="h5"
        />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          No sections found in this template.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <LayoutWrapper>
      <LayoutWrapper.TopBar>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            pr: { xs: 2, md: 4 },
            py: 0.5,
          }}
        >
          <RoundedButton
            variant="contained"
            color="primary"
            sx={{
              fontSize: '0.85rem',
              fontWeight: '700',
              whiteSpace: 'nowrap',
              px: 2,
              py: 0.5,
              minWidth: '152px',
            }}
            onClick={() => navigate(`/note/${noteId}`)}
          >
            Done
          </RoundedButton>
        </Box>
      </LayoutWrapper.TopBar>
      <LayoutWrapper.MainContent>
        <Box
          sx={{
            pt: 4,
            pb: 8,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          {/* Page Heading with Template Name Emphasis */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label="Back to note"
              sx={{ alignSelf: 'flex-start' }}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <EditableTemplateHeading
                templateName={templateName}
                onSave={handleSaveTemplateName}
                canEdit={noteData?.can_edit_template ?? false}
              />
            </Box>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.BeforeDragging,
              },
            }}
          >
            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {/* Add section button at the top */}
              <BetweenItemAddButton
                enabled={activeId === null}
                onClick={() => handleAdd(-1)}
                label="Section"
              />

              {/* Render all sections with add buttons */}
              {sections.map((section, index) => (
                <React.Fragment key={section.id}>
                  <TemplateSectionItem
                    section={section}
                    onRemove={handleRemove}
                  />

                  {/* Add button after each section */}
                  <BetweenItemAddButton
                    enabled={activeId === null}
                    onClick={() => handleAdd(index)}
                    label="Section"
                  />
                </React.Fragment>
              ))}
            </SortableContext>

            {/* DragOverlay - renders dragged item in portal */}
            <DragOverlay
              dropAnimation={
                validDrop
                  ? null // No animation on successful drop to prevent flash
                  : {
                      duration: 500,
                      easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                    }
              }
            >
              {(() => {
                const activeSection = activeId ? sections.find(s => s.id === activeId) : null;
                return activeSection ? (
                  <Paper
                    elevation={8}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: 'grabbing',
                      opacity: 0.9,
                      border: 2,
                      borderColor: 'primary.main',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {activeSection.title}
                    </Typography>
                  </Paper>
                ) : null;
              })()}
            </DragOverlay>
          </DndContext>
        </Box>
      </LayoutWrapper.MainContent>
      </LayoutWrapper>
      {/* Module Selector Modal - Outside LayoutWrapper for proper z-index */}
      <ModuleSelector
        open={isModuleSelectorOpen}
        onClose={handleCloseModal}
        onAddModules={handleAddModules}
        excludedModules={excludedModuleIds}
        source="note-template-editor"
        templateId={noteData?.template?.template_id ?? undefined}
        templateName={noteData?.template?.template_name ?? undefined}
      />
    </>
  );
};
